import type { AuditFormProps } from "../../../types/audit-form";
import type { AuditFileMetadata } from "../../../types/audit-file";
import { useLatest } from "../../../hooks/use-latest";
import { Eye, Loader2, Plus, Save, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useImageDescriptions } from "../../../hooks/use-image-descriptions";
import { useAppDispatch } from "../../../redux/hooks";
import {
  deleteFile,
  fetchFiles,
  updateFile,
  uploadMultipleFiles,
} from "../../../redux/slices/fileSlice.js";
import {
  createWork,
  deleteWork,
  fetchWorks,
  updateWork,
} from "../../../redux/slices/workSlice.js";
import {
  errorMessage,
  requireMutationSuccess,
} from "../../../utils/mutationResult.js";
import { OfflineImage } from "../../shared/offline-image";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card.jsx";
import { Label } from "../../ui/label.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
// Add these imports at the top with other imports
import { useDebouncedSave } from "../../../hooks/use-debounced-save";
import { useDraftAutosave } from "../../../hooks/use-draft-autosave";
import {
  useVersionAwareFiles,
  useVersionAwareSiteData,
  useVersionAwareWorks,
} from "../../../hooks/use-version-aware-data";
import { useNotification } from "../../../hooks/useNotification";
import { updateSiteData } from "../../../redux/slices/siteDataSlice.js";
import { punchListCompletedSampleTableProgress } from "../../../utils/componentUtils.js";
import { notificationHelpers } from "../../../utils/notificationHelpers";
import { LoadingIndicator } from "../../ui/loading-indicator";
import {
  OptimizedInput,
  OptimizedSelect,
  OptimizedTextarea,
} from "../optimized-form-field";
import { ImageCapturePicker } from "../shared/image-capture-picker";
import { LocalSectionImportControl } from "../shared/local-section-import-control";
// Add this after the interfaces, before the initialWorkItems
const PREDEFINED_DESCRIPTIONS = {
  mechanical: [
    "Mounting Structure",
    "Galvanization",
    "Material Selection",
    "Module Clamps",
    "Inverter structure",
    "End Protection",
    // "Purlin and attachments installation",
    // "Torque application on nuts & bolts",
    // "Module alignment and positioning",
    // "Mechanical corrosion inspection",
    // "Perimeter fencing installation"
  ],
  electrical: [
    "Modules",
    "Wiring",
    "Cable Management",
    "Inverter",
    "Ac Isolator",
    "Earthing",

    // "DC String Cable Management - above ground",
    // "DC String Cable Management - underground",
    // "LV/AC cable management above ground",
    // "LV/AC cable management underground",
    // "HV cables between transformers",
    // "HV cables to substation",
    // "Inverter installation and commissioning",
    // "AC isolator installation",
    // "Earthing system installation",
    // "Cable gland sealing and termination"
  ],
  civil: [
    "Foundation",
    "Trenching",
    // "Foundation preparation and pouring",
    // "Trenching works completion",
    // "Transformer foundation construction",
    // "DNO substation foundation",
    // "CCTV foundation installation",
    // "Combiner box foundation",
    // "Inverter foundation construction",
    // "Site access road construction",
    // "Drainage systems installation",
    // "Site cleanup and restoration"
  ],
};

interface CompletedWorksFormProps extends AuditFormProps {
  siteId: string;
  reportId?: string;
  templateId: string;
  readOnly?: boolean;
  isViewingHistoricalVersion?: boolean;
  versionData?: any;
  previousCompletedReports?: any[];
}

interface WorkImage extends AuditFileMetadata {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  description: string;
  uploadDate: Date;
}

interface WorkItem {
  id: string;
  description: string;
  comments: string;
  completed: boolean;
  images: WorkImage[];
}

interface CompletedWorksData {
  mechanical: WorkItem[];
  electrical: WorkItem[];
  civil: WorkItem[];
  all: WorkItem[];
}

export function CompletedWorksForm({
  siteId,
  reportId,
  templateId,
  setSessionProgress,
  readOnly = false,
  sessionProgress,
  previousCompletedReports = [],
}: CompletedWorksFormProps) {
  const { notify } = useNotification();

  const [workData, setWorkData] = useState<Record<string, Partial<WorkItem>>>(
    {},
  );
  const [activeTab, setActiveTab] =
    useState<keyof CompletedWorksData>("mechanical");
  const [selectedImage, setSelectedImage] = useState<WorkImage | null>(null);
  // Add this state for showing custom input
  const [showCustomDescription, setShowCustomDescription] = useState<{
    [key: string]: boolean;
  }>({});
  const [formData, setFormData] = useState({ completedWorksDescription: "" });
  const formDataRef = useLatest(formData);

  const [uploadingWorkImages, setUploadingWorkImages] = useState<
    Record<string, boolean>
  >({});
  const [workProgressData, setWorkProgressData] = useState([]);

  const dispatch = useAppDispatch();

  const currentSiteData = useVersionAwareSiteData(readOnly);
  const currentWorksData = useVersionAwareWorks(readOnly);
  const files = useVersionAwareFiles(readOnly);
  const [imageDescriptions, setImageDescriptions] = useImageDescriptions(files);

  // Autosave unsaved edits to IndexedDB so nothing is lost if the app closes
  // before the debounced server save fires (e.g. offline, tab closed, phone
  // loses power).
  const draftKey =
    !readOnly && reportId ? `report:${reportId}:completed-sample-table` : null;
  const { restoreDraft, clearSavedDraft } = useDraftAutosave(
    draftKey,
    formData,
  );
  const draftRestoredRef = useRef(false);

  useEffect(() => {
    if (!draftKey || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    restoreDraft().then((draft) => {
      if (typeof draft?.data?.completedWorksDescription === "string") {
        // Older drafts may contain the entire punchList. Restore only this form.
        setFormData({
          completedWorksDescription: draft.data.completedWorksDescription,
        });
      }
    });
  }, [draftKey, restoreDraft]);

  useEffect(() => {
    if (!draftKey || !currentSiteData) return;
    if (
      JSON.stringify({
        completedWorksDescription:
          currentSiteData?.punchList?.completedWorksDescription || "",
      }) === JSON.stringify(formData)
    ) {
      clearSavedDraft();
    }
  }, [draftKey, currentSiteData, formData, clearSavedDraft]);

  useEffect(() => {
    const completedWorksProgress = punchListCompletedSampleTableProgress(
      workProgressData,
      formData?.completedWorksDescription,
    );

    setSessionProgress((prev) => ({
      ...prev,
      [templateId]: {
        ...prev[templateId],
        "completed-sample-table": completedWorksProgress,
      },
    }));
  }, [workProgressData, formData, templateId, files, setSessionProgress]);

  const handleSaveWork = useCallback(
    async (data: typeof workData) => {
      try {
        const importedWorkEntries = workProgressData
          .filter((work: any) => work?._sourceId && !work?._id)
          .map((work: any) => [work.id, work] as [string, Partial<WorkItem>]);
        const workEntries = [
          ...Object.entries(data),
          ...importedWorkEntries.filter(([workId]) => !data[workId]),
        ];

        const mutationResult1 = await Promise.all(
          workEntries.map(([workId, pendingWorkData]) => {
            const existingWork = workProgressData.find(
              (work: any) => work?._id === workId || work?.id === workId,
            );
            const { _id, ...existingWorkData } = existingWork || {};
            const isImportedWork =
              String(workId).startsWith("imported-") || !existingWork?._id;

            const payload = {
              ...(existingWorkData || {}),
              // workType: existingWork?.workType || pendingWorkData?.workType,
              // comments: existingWork?.comments || pendingWorkData?.comments,
              ...pendingWorkData,
              siteId,
              templateId,
              workCategory: "completed-works",
            };
            return isImportedWork
              ? requireMutationSuccess(dispatch(createWork(payload as any)))
              : dispatch(updateWork({ workId, workData: payload })).unwrap();
          }),
        );
        const config = notificationHelpers.formSaveSuccess("Work Data", [
          mutationResult1,
        ]);
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Work Data",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
        throw error;
      }
    },
    [dispatch, notify, workProgressData, siteId, templateId],
  );

  const handleSaveForm = useCallback(
    async (data: typeof formData) => {
      if (!siteId) return;

      try {
        const mutationResult2 = await requireMutationSuccess(
          dispatch(
            updateSiteData({
              siteDataId: siteId,
              siteDataInfo: {
                reportId,
                punchList: {
                  completedWorksDescription:
                    data.completedWorksDescription || "",
                },
              },
            }),
          ),
        );
        const config = notificationHelpers.formSaveSuccess("Form Data", [
          mutationResult2,
        ]);
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Form Data",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
        throw error;
      }
    },
    [dispatch, siteId, reportId, notify],
  );

  const {
    isSaving: isWorkSaving,
    lastSaved: lastSavedWork,
    hasUnsavedChanges: hasWorkUnsavedChanges,
    saveError: workSaveError,
    forceSave: forceWorkSave,
  } = useDebouncedSave(workData, {
    delay: 7500,
    onSave: handleSaveWork,
    onError: (error) => console.error("Work save error:", error),
  });

  const {
    isSaving: isFormSaving,
    lastSaved: lastSavedForm,
    hasUnsavedChanges: hasFormUnsavedChanges,
    saveError: formSaveError,
    forceSave: forceFormSave,
    resetBaseline: resetFormBaseline,
  } = useDebouncedSave(formData, {
    delay: 7500,
    onSave: handleSaveForm,
    onError: (error) => console.error("Form save error:", error),
  });

  useEffect(() => {
    const newFormData = {
      ...formDataRef.current,
      completedWorksDescription:
        currentSiteData?.punchList?.completedWorksDescription || "",
    };
    resetFormBaseline(newFormData);
    setFormData(newFormData);
  }, [currentSiteData, resetFormBaseline, formDataRef]);

  // Child work mutations must not rehydrate or mark the summary as saved.
  useEffect(() => {
    setWorkProgressData(
      currentWorksData?.filter((w) => w.workCategory === "completed-works") ??
        [],
    );
  }, [currentWorksData]);

  // Save handler for image descriptions
  const handleSaveImages = useCallback(
    async (imageDescriptions: Record<string, string>) => {
      try {
        const mutationResult3 = await Promise.all(
          Object.entries(imageDescriptions).map(([fileId, fileDescription]) =>
            requireMutationSuccess(
              dispatch(updateFile({ fileId, fileData: { fileDescription } })),
            ),
          ),
        );

        const config = notificationHelpers.formSaveSuccess(
          "Image Descriptions",
          [mutationResult3],
        );
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Image Descriptions",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);

        throw error;
      }
    },
    [dispatch, notify],
  );

  // Debounced save for image descriptions (5000ms delay)
  const {
    isSaving: isImageSaving,
    lastSaved: lastSavedImage,
    hasUnsavedChanges: hasImageUnsavedChanges,
    saveError: imageSaveError,
    forceSave: forceImageSave,
  } = useDebouncedSave(imageDescriptions, {
    delay: 5000,
    onSave: handleSaveImages,
    onError: (error) => console.error("Image description save error:", error),
    readOnly,
  });

  // Combine form and image save states
  const isSaving = isFormSaving || isWorkSaving || isImageSaving;
  const hasUnsavedChanges =
    hasFormUnsavedChanges || hasWorkUnsavedChanges || hasImageUnsavedChanges;
  const saveError = formSaveError || workSaveError || imageSaveError;
  const lastSaved = useMemo(() => {
    const dates = [lastSavedForm, lastSavedImage, lastSavedWork].filter(
      Boolean,
    );
    if (dates.length === 0) {
      return null;
    }

    return new Date(Math.max(...dates.map((date) => new Date(date).getTime())));
  }, [lastSavedForm, lastSavedImage, lastSavedWork]);

  const forceSave = useCallback(() => {
    forceFormSave();
    forceImageSave();
    forceWorkSave();
  }, [forceFormSave, forceImageSave, forceWorkSave]);

  useEffect(() => {
    dispatch(fetchWorks({ siteId, templateId, reportId }));
    dispatch(
      fetchFiles({
        siteId,
        reportId,
      }),
    );
  }, [siteId, templateId, reportId, dispatch]);

  // Add this function to get description options
  const getDescriptionOptions = (category: keyof CompletedWorksData) => {
    const predefinedOptions =
      PREDEFINED_DESCRIPTIONS[category]?.map((desc) => ({
        value: desc,
        label: desc,
      })) ?? [];

    return [
      // { value: "select", label: "Select here" },
      ...predefinedOptions,
      // { value: "custom", label: "+ Custom Description" },
    ];
  };
  // Add this function to toggle custom description input
  const toggleCustomDescription = (itemId: string) => {
    setShowCustomDescription((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const getLoadingStatus = () => {
    if (saveError) return "error";
    if (isSaving) return "saving";
    if (hasUnsavedChanges) return "unsaved";
    if (lastSaved) return "saved";
    return "idle";
  };

  const updateWorkItem = (
    category: keyof CompletedWorksData,
    itemId: string,
    field: keyof WorkItem,
    value: string | boolean | WorkImage[],
  ) => {
    setWorkProgressData((prev) =>
      prev.map((work) =>
        work.id === itemId || work._id === itemId || work._clientId === itemId
          ? {
              ...work,
              [field]: value,
            }
          : work,
      ),
    );
    setWorkData((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value,
      },
    }));
  };

  const getWorkValue = <K extends keyof WorkItem>(
    item: WorkItem,
    field: K,
  ): WorkItem[K] =>
    field in (workData[item.id] || {})
      ? (workData[item.id][field] as WorkItem[K])
      : item[field];

  const addWorkItem = (category: keyof CompletedWorksData) => {
    const newItem = {
      siteId: siteId,
      templateId: templateId,
      reportId: reportId,
      workType: category,
      workCategory: "completed-works",
    };

    try {
      requireMutationSuccess(dispatch(createWork(newItem)))
        .then((mutationOutcome) => {
          const config = notificationHelpers.actionCreate(
            "Work",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          const config = notificationHelpers.actionCreateError(
            "Work",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        });
    } catch (error) {
      const config = notificationHelpers.actionCreateError(
        "Work",
        errorMessage(error),
      );
      notify(config.message, config.type, config.options);
    }
  };

  const removeWorkItem = (
    category: keyof CompletedWorksData,
    itemId: string,
  ) => {
    try {
      dispatch(deleteWork(itemId))
        .unwrap()
        .then((mutationOutcome) => {
          setWorkProgressData((prev: any[]) =>
            prev.filter(
              (work: any) =>
                work.id !== itemId &&
                work._id !== itemId &&
                work._clientId !== itemId,
            ),
          );
          setWorkData((prev: Record<string, Partial<WorkItem>>) => {
            const next = { ...prev };
            delete next[itemId];
            return next;
          });
          setShowCustomDescription((prev: Record<string, boolean>) => {
            const next = { ...prev };
            delete next[itemId];
            return next;
          });

          const config = notificationHelpers.actionDelete(
            "Work",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          const config = notificationHelpers.actionDeleteError(
            "Work",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        });
    } catch (error) {
      const config = notificationHelpers.actionDeleteError(
        "Activity",
        errorMessage(error),
      );
      notify(config.message, config.type, config.options);
    }
  };

  const handleImageUpload = (
    filterData,
    itemId: string,
    files: FileList | File[] | null,
  ) => {
    if (!files) return;

    const fileArray = Array.from(files);
    setUploadingWorkImages((prev) => ({ ...prev, [itemId]: true }));

    requireMutationSuccess(
      dispatch(
        uploadMultipleFiles({
          files: fileArray, // actual File objects
          metadata: {
            siteId: siteId,
            entityType: "completed-works",
            entityId: itemId,
            reportId,
            // You can add per-file metadata here if needed
          },
          filterData,
        }),
      ),
    )
      .then((mutationOutcome) => {
        // Show success notification for each uploaded file
        fileArray.forEach((file) => {
          const config = notificationHelpers.imageUploadSuccess(
            file.name,
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        });
      })
      .catch((error) => {
        // Show error notification
        const config = notificationHelpers.imageUploadError(
          fileArray.length === 1
            ? fileArray[0].name
            : `${fileArray.length} files`,
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
      })
      .finally(() => {
        setUploadingWorkImages((prev) => ({ ...prev, [itemId]: false }));
      });
  };

  // Update image description — updates local state; debounced save handles the dispatch
  const updateImageDescription = useCallback(
    (imageId: string, description: string) => {
      setImageDescriptions((prev) => ({ ...prev, [imageId]: description }));
    },
    [setImageDescriptions],
  );

  const removeImage = (
    category: keyof CompletedWorksData,
    itemId: string,
    imageId: string,
  ) => {
    requireMutationSuccess(dispatch(deleteFile(imageId)))
      .then((mutationOutcome) => {
        const config = notificationHelpers.imageDeleteSuccess(
          "Image",
          mutationOutcome,
        );
        notify(config.message, config.type, config.options);
      })
      .catch((error) => {
        const config = notificationHelpers.imageDeleteError(
          "Image",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
      });
    // const currentItem = formData[category].find((item) => item.id === itemId);
    // if (currentItem) {
    //   const updatedImages = currentItem.images.filter(
    //     (img) => img.id !== imageId
    //   );
    //   updateWorkItem(category, itemId, "images", updatedImages);
    // }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  const getCompletionStats = (category: keyof CompletedWorksData) => {
    const items =
      category === "all"
        ? workProgressData?.filter(
            (work) => work?.workCategory === "completed-works",
          )
        : workProgressData?.filter(
            (work) =>
              work?.workCategory === "completed-works" &&
              work?.workType === category,
          );
    const completed = items.filter((item) => item.completed).length;
    const total = items.length;

    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  };

  // Update the renderWorksTable function to use OptimizedSelect
  const renderWorksTable = (category: keyof CompletedWorksData) => {
    const items =
      category === "all"
        ? workProgressData?.filter(
            (work) => work?.workCategory === "completed-works",
          ) || []
        : workProgressData?.filter(
            (work) =>
              work?.workCategory === "completed-works" &&
              work?.workType === category,
          ) || [];
    return (
      <div className="space-y-4">
        {items.map((item) => {
          const workId = item.id || item._id || item._clientId; // Use either id or _id based on availability
          return (
            <Card key={workId} className="p-4">
              <div className="space-y-4">
                <div className="flex items-center justify-end">
                  {/* <OptimizedCheckbox
                  id={`completed-${workId}`}
                  label="Complete"
                  checked={getWorkValue(item, "completed")}
                  onChange={(e) =>
                    updateWorkItem(item?.workType, workId, "completed", e)
                  }
                  readOnly={readOnly}
                /> */}

                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeWorkItem(item?.workType, workId)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Updated Work Description Section with OptimizedSelect */}
                <div className="space-y-2">
                  <OptimizedSelect
                    label="Work Description"
                    id={`description-${workId}`}
                    value={getWorkValue(item, "description") || ""}
                    onChange={(value) => {
                      if (value === "custom") {
                        toggleCustomDescription(workId);
                        updateWorkItem(
                          item?.workType,
                          workId,
                          "description",
                          "",
                        );
                      } else if (value !== "select") {
                        updateWorkItem(
                          item?.workType,
                          workId,
                          "description",
                          value,
                        );
                        setShowCustomDescription((prev) => ({
                          ...prev,
                          [workId]: false,
                        }));
                      }
                    }}
                    placeholder="Select Work Description"
                    options={getDescriptionOptions(item?.workType)}
                    readOnly={readOnly}
                    required
                  />
                  {showCustomDescription[workId] && (
                    <OptimizedInput
                      id={`custom-description-${workId}`}
                      value={getWorkValue(item, "description") || ""}
                      readOnly={readOnly}
                      onChange={(value) =>
                        updateWorkItem(
                          item?.workType,
                          workId,
                          "description",
                          value,
                        )
                      }
                      placeholder="Enter custom work description..."
                      required
                    />
                  )}
                </div>

                {/* Updated Comments Section with OptimizedTextarea */}
                <div className="space-y-2">
                  <Label
                    htmlFor={`${item?.workType}-${workId}-comments`}
                    className="flex items-center gap-1"
                  >
                    Comments & Notes
                    <span className="text-red-500">*</span>
                  </Label>
                  <OptimizedTextarea
                    id={`${item?.workType}-${workId}-comments`}
                    value={getWorkValue(item, "comments") || ""}
                    readOnly={readOnly}
                    onChange={(value) =>
                      updateWorkItem(item?.workType, workId, "comments", value)
                    }
                    placeholder="Add progress notes, observations, or additional details..."
                    rows={3}
                  />
                </div>

                {/* Rest of the code remains the same */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Work Images</label>
                    {!readOnly && (
                      <ImageCapturePicker
                        buttonText="Add Images"
                        isUploading={uploadingWorkImages[workId] || false}
                        disabled={false}
                        onConfirm={(selectedFiles) =>
                          handleImageUpload(
                            { [item?.workType]: true },
                            workId,
                            selectedFiles,
                          )
                        }
                      />
                    )}
                  </div>

                  {uploadingWorkImages[workId] && (
                    <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 py-6 text-sm text-slate-600 dark:bg-muted dark:text-muted-foreground dark:border-border">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Uploading images...
                    </div>
                  )}

                  {files?.filter((file) => file?.relatedTo?.entityId === workId)
                    ?.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {files
                        ?.filter((file) => file?.relatedTo?.entityId === workId)
                        ?.map((image) => (
                          <div key={image.id} className="relative group">
                            <div className="aspect-video bg-slate-100 rounded overflow-hidden dark:bg-muted">
                              <OfflineImage
                                fileId={
                                  image?._id || image?.id || image?._clientId
                                }
                                src={image.fileUrl || "/placeholder.svg"}
                                alt="Work progress"
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="absolute top-1 right-1 flex gap-1">
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity
                             bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                                onClick={() => setSelectedImage(image)}
                              >
                                <Eye className="w-3 h-3" />
                              </Button>
                              {!readOnly && (
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity
                             bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                                  onClick={() =>
                                    removeImage(
                                      item?.workType,
                                      workId,
                                      image.id,
                                    )
                                  }
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                            <div className="mt-1 space-y-1">
                              <div className="flex items-center justify-between">
                                <div className="text-xs font-medium text-slate-700 truncate dark:text-foreground">
                                  {image.originalName}
                                </div>
                                <Badge variant="outline" className="text-xs">
                                  {formatFileSize(image.fileSize)}
                                </Badge>
                              </div>
                              <OptimizedTextarea
                                id={`image-description-${image.id}`}
                                value={
                                  imageDescriptions[image.id] ??
                                  image.fileDescription ??
                                  ""
                                }
                                readOnly={readOnly}
                                onChange={(value) =>
                                  updateImageDescription(image.id, value)
                                }
                                placeholder="Describe this image (optional)..."
                                rows={1}
                              />
                              <div className="text-xs text-slate-500 dark:text-muted-foreground">
                                Date:{" "}
                                {new Date(
                                  image.uploadDate,
                                ).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  const overallStats = {
    mechanical: getCompletionStats("mechanical"),
    electrical: getCompletionStats("electrical"),
    civil: getCompletionStats("civil"),
    all: getCompletionStats("all"),
  };

  const overallPercentage =
    sessionProgress[templateId]?.["completed-sample-table"];

  return (
    <div className="space-y-4 md:space-y-6">
      {!readOnly && (
        <LocalSectionImportControl
          previousCompletedReports={previousCompletedReports}
          sectionName="completed-sample-table"
          siteId={siteId}
          reportId={reportId}
          readOnly={readOnly}
          setFormData={setFormData}
          workProgressData={workProgressData}
          setWorkProgressData={setWorkProgressData}
        />
      )}
      {/* Floating save section with tooltip - hidden when viewing read-only version */}
      {!readOnly && (
        <div className="fixed top-25 right-3 z-40 sm:right-4">
          <div className="flex flex-row items-center gap-2 sm:flex-col sm:items-end sm:gap-3">
            {/* Status indicator - same size as save button */}
            {false && (
              <div
                className="
          bg-white/95 backdrop-blur-sm
          rounded-full border-2 border-gray-200/80
          shadow-lg
          w-12 h-12 sm:w-16 sm:h-16
          flex items-center justify-center
          group relative
          transition-all duration-300 ease-out
          transform hover:scale-110
          cursor-default
          p-2
        "
              >
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <span className="text-[10px] font-medium text-gray-600 leading-none mb-2 dark:text-muted-foreground">
                    Saved
                  </span>

                  <div className="flex items-center justify-center h-6">
                    <LoadingIndicator
                      status={getLoadingStatus()}
                      lastSaved={lastSaved}
                      error={saveError}
                    />
                  </div>
                </div>

                {/* Tooltip - Fixed with pointer-events-none */}
                <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  {getLoadingStatus() === "saving"
                    ? "Saving..."
                    : getLoadingStatus() === "saved"
                      ? `Saved ${lastSaved?.toLocaleTimeString()}`
                      : getLoadingStatus() === "error"
                        ? "Save failed"
                        : "Ready to save"}
                </div>

                {/* Error indicator */}
                {getLoadingStatus() === "error" && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                  </div>
                )}
              </div>
            )}

            {/* Save button - Fixed with pointer-events-none */}
            <Button
              onClick={forceSave}
              disabled={isSaving || readOnly}
              className={`
          relative rounded-full w-12 h-12 sm:w-16 sm:h-16
          transition-all duration-300 ease-out
          flex flex-col items-center justify-center
          backdrop-blur-sm border-2
          shadow-lg hover:shadow-xl
          transform hover:scale-110 active:scale-95
          group
          ${
            hasUnsavedChanges && !readOnly
              ? "bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white border-red-300 dark:bg-gradient-to-br dark:border-red-300 dark:ring-red-300"
              : "bg-white/95 text-gray-600 border-gray-200/80 hover:bg-white/90 dark:bg-muted dark:text-muted-foreground dark:border-border dark:hover:bg-accent"
          }
          ${isSaving || readOnly ? "opacity-80 cursor-not-allowed" : "cursor-pointer"}
        `}
            >
              {isSaving ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span className="text-[10px] leading-none">Saving</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <Save className="w-5 h-5 sm:mb-1" />
                  <span className="hidden text-[10px] font-medium leading-none sm:block">
                    Save
                  </span>
                </div>
              )}

              {/* Unsaved changes indicator */}
              {hasUnsavedChanges && !isSaving && (
                <div className="absolute -top-1 -right-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                  <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                </div>
              )}

              {/* Tooltip - Fixed with pointer-events-none */}
              <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                {hasUnsavedChanges ? "Save changes" : "No changes to save"}
              </div>
            </Button>
          </div>
        </div>
      )}
      <Card>
        <CardContent>
          <div className="space-y-2">
            <OptimizedTextarea
              label="Completed Sample Table Description"
              id={`completed-sample-table-description`}
              readOnly={readOnly}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  completedWorksDescription: value,
                }))
              }
              value={formData.completedWorksDescription}
              placeholder="Enter any description regarding the completed sample table..."
              rows={3}
              required
            />
          </div>
        </CardContent>
      </Card>

      {/* <Card>
        <CardHeader>
          <CardTitle>Overall Works Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {overallPercentage}%
              </div>
              <div className="text-sm text-slate-600">Overall Complete</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {overallStats.mechanical.percentage}%
              </div>
              <div className="text-sm text-slate-600">Mechanical</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {overallStats.electrical.percentage}%
              </div>
              <div className="text-sm text-slate-600">Electrical</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {overallStats.civil.percentage}%
              </div>
              <div className="text-sm text-slate-600">Civil</div>
            </div>
          </div>
        </CardContent>
      </Card> */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              Works
              {/* <Badge
                  variant={stats.percentage === 100 ? "default" : "secondary"}
                >
                  {stats.completed}/{stats.total} Complete
                </Badge> */}
            </CardTitle>

            {!readOnly && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => addWorkItem("mechanical")}
                  variant="outline"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Mechanical
                </Button>
                <Button
                  size="sm"
                  onClick={() => addWorkItem("electrical")}
                  variant="outline"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Electrical
                </Button>
                <Button
                  size="sm"
                  onClick={() => addWorkItem("civil")}
                  variant="outline"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Civil
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Tabs
            value={activeTab}
            onValueChange={(value: keyof CompletedWorksData) =>
              setActiveTab(value)
            }
          >
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger
                value="all"
                className="flex items-center gap-2 bg-muted-foreground"
              >
                All
                <Badge variant="outline" className="text-xs">
                  {overallStats.all.completed}/{overallStats.all.total}
                </Badge>
              </TabsTrigger>

              <TabsTrigger
                value="mechanical"
                className="flex items-center gap-2 bg-muted-foreground"
              >
                Mechanical
                <Badge variant="outline" className="text-xs">
                  {overallStats.mechanical.completed}/
                  {overallStats.mechanical.total}
                </Badge>
              </TabsTrigger>
              <TabsTrigger
                value="electrical"
                className="flex items-center gap-2 bg-muted-foreground"
              >
                Electrical
                <Badge variant="outline" className="text-xs">
                  {overallStats.electrical.completed}/
                  {overallStats.electrical.total}
                </Badge>
              </TabsTrigger>
              <TabsTrigger
                value="civil"
                className="flex items-center gap-2 bg-muted-foreground"
              >
                Civil
                <Badge variant="outline" className="text-xs">
                  {overallStats.civil.completed}/{overallStats.civil.total}
                </Badge>
              </TabsTrigger>
            </TabsList>
            <TabsContent value={activeTab} className="mt-6">
              {renderWorksTable(activeTab)}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      {/*
      <TabsContent value="mechanical" className="mt-6">
          {renderWorksTable("mechanical", "Mechanical")}
        </TabsContent>

        <TabsContent value="electrical" className="mt-6">
          {renderWorksTable("electrical", "Electrical")}
        </TabsContent>

        <TabsContent value="civil" className="mt-6">
          {renderWorksTable("civil", "Civil")}
        </TabsContent> */}

      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-4xl max-h-full overflow-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {selectedImage.originalName}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedImage(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4">
              <OfflineImage
                fileId={
                  selectedImage?._id ||
                  selectedImage?.id ||
                  selectedImage?._clientId
                }
                src={selectedImage?.fileUrl || "/placeholder.svg"}
                alt={selectedImage?.originalName}
                className="max-w-full max-h-96 mx-auto"
              />
              {selectedImage.fileDescription && (
                <div className="mt-4 p-3 bg-slate-50 dark:bg-muted rounded">
                  <p className="text-sm text-slate-700 dark:text-muted-foreground">
                    {selectedImage.fileDescription}
                  </p>
                </div>
              )}
              <div className="mt-2 text-sm text-slate-500 dark:text-muted-foreground">
                Size: {formatFileSize(selectedImage.fileSize)} • Date:{" "}
                {new Date(selectedImage.uploadDate).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="sticky bottom-2 md:bottom-4 z-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-white dark:bg-card rounded-lg border shadow-lg backdrop-blur-sm bg-white/95 dark:bg-surface-raised/95">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
            Works Status:
          </span>
          <Badge variant={overallPercentage === 100 ? "default" : "secondary"}>
            {overallPercentage === 100
              ? "All Complete"
              : `${overallPercentage}% Complete`}
          </Badge>
        </div>
        <div className="text-xs md:text-sm text-slate-500 dark:text-muted-foreground hidden lg:block">
          Auto-save enabled • Changes saved automatically
        </div>
      </div>
    </div>
  );
}
